/**
 * Memoria a Corto Plazo — replica MemoriaEvocacionReconocTest.aspx.cs
 *
 * Procesa la Secuencia en orden, igual que SiguienteSecuencia() en ASP.NET:
 *   es_consigna        → muestra texto de instrucción + botón OK
 *   es_pregunta_boolena → muestra palabra + Sí/No
 *   else               → split Valor por ';', muestra cada palabra (tiempo_mili ms), luego recall
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getTestConfig, TYPE_TO_API_PATH } from '../../api/client';
import TestLayout, {
  DoneScreen, YesNoButtons, LoadingScreen, ErrorScreen, useSpeedMultiplier,
  useFocusOnStep,
} from './shared/TestLayout';
import { registrarUso } from '../../api/usoClient';
import SaveResults from './shared/SaveResults';
import { navigateAfterTest } from '../../api/ensayosClient';
import SafeHtml from './shared/SafeHtml';

function showReconocimientoInfo() {
  Swal.fire({
    title: 'Indicadores de reconocimiento',
    html: `
      <div style="text-align:left; line-height:1.8; font-size:14px">
        <p><span style="color:#16a34a">✓ <strong>Reconocidas</strong></span><br>
        Palabras que <em>sí estaban</em> en la lista y el usuario dijo <strong>Sí</strong>.</p>
        <p><span style="color:#d97706">⚠ <strong>Omisiones</strong></span><br>
        Palabras que <em>sí estaban</em> en la lista pero el usuario dijo <strong>No</strong>.</p>
        <p><span style="color:#dc2626">✗ <strong>Falsas alarmas</strong></span><br>
        Palabras que <em>no estaban</em> en la lista pero el usuario dijo <strong>Sí</strong>.</p>
      </div>`,
    icon: 'info',
    confirmButtonText: 'Entendido',
    confirmButtonColor: '#6366f1',
  });
}

const PHASE = {
  LOADING:      'loading',
  CONSIGNA:     'consigna',
  PRESENTATION: 'presentation',
  RECALL:       'recall',
  RECOGNITION:  'recognition',
  DONE:         'done',
};

// Quita tildes/diacríticos para comparación tolerante (limón = limon)
function normWord(s) {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export default function TestRunMemoria() {
  const { lenguajeId, categoriaId, tareaId, type, testId } = useParams();
  const navigate  = useNavigate();
  const apiType   = TYPE_TO_API_PATH[type] ?? type;

  const [config, setConfig]   = useState(null);
  const [error, setError]     = useState(null);
  const [phase, setPhase]     = useState(PHASE.LOADING);

  // índice en secuencia[]
  const [seqIndex, setSeqIndex]     = useState(0);
  // palabras del item actual (Valor.split(';'))
  const [wordList, setWordList]     = useState([]);
  const [wordIndex, setWordIndex]   = useState(0);

  // acumulan respuestas
  const [recallText, setRecallText]   = useState('');
  const [responses, setResponses]     = useState([]); // { type, item, userAnswer }

  const [showResults, setShowResults] = useState(false);

  const timerRef       = useRef(null);
  const usageLoggedRef = useRef(false);
  const responsesRef   = useRef([]);
  responsesRef.current = responses;

  useEffect(() => {
    getTestConfig(apiType, testId)
      .then((data) => { setConfig(data); setPhase(PHASE.LOADING); })
      .catch((e) => setError(e.message));
  }, [apiType, testId]);

  const speedMultiplier = useSpeedMultiplier();
  const secuencia   = config?.ListaValores?.Secuencia ?? [];
  const tiempoMili  = (config?.tiempo_mili ?? 1500) / speedMultiplier;
  const conclusiones = config?.Concluciones ?? '';

  const goBack = useCallback(() => {
    navigateAfterTest(
      navigate,
      `/lenguaje/${lenguajeId}/categoria/${categoriaId}/tarea/${tareaId}`,
    );
  }, [navigate, lenguajeId, categoriaId, tareaId]);

  useEffect(() => {
    if (phase !== PHASE.DONE || usageLoggedRef.current) return;
    usageLoggedRef.current = true;
    const r = responsesRef.current;
    const reconocimiento = r.filter((x) => x.type === 'recognition');
    const correctas = reconocimiento.filter((x) => x.userYes === !!x.item.es_verdadero).length;
    registrarUso({
      tipo_test:   `MAC${testId}`,
      test_id:     testId,
      tarea_id:    tareaId,
      nombre_test: config?.descripcion ?? '',
      completada:  true,
      total_items: reconocimiento.length,
      correctas,
      errores:     reconocimiento.length - correctas,
    });
  }, [phase, testId, tareaId, config]);

  const handleExit = useCallback(() => {
    if (!usageLoggedRef.current) {
      usageLoggedRef.current = true;
      const r = responsesRef.current;
      const reconocimiento = r.filter((x) => x.type === 'recognition');
      const correctas = reconocimiento.filter((x) => x.userYes === !!x.item.es_verdadero).length;
      registrarUso({
        tipo_test:   `MAC${testId}`,
        test_id:     testId,
        tarea_id:    tareaId,
        nombre_test: config?.descripcion ?? '',
        completada:  false,
        total_items: reconocimiento.length,
        correctas,
        errores:     reconocimiento.length - correctas,
      });
    }
    goBack();
  }, [goBack, testId, tareaId, config]);

  // Avanza al siguiente item de la secuencia
  const advance = useCallback((newResponses) => {
    const next = seqIndex + 1;
    if (next >= secuencia.length) {
      setResponses(newResponses);
      setPhase(PHASE.DONE);
      return;
    }
    const item = secuencia[next];
    setSeqIndex(next);

    if (item.es_consigna) {
      setResponses(newResponses);
      setPhase(PHASE.CONSIGNA);
    } else if (item.es_pregunta_boolena) {
      setResponses(newResponses);
      setPhase(PHASE.RECOGNITION);
    } else {
      const words = String(item.Valor ?? '').split(';').map((w) => w.trim()).filter(Boolean);
      setWordList(words);
      setWordIndex(0);
      setResponses(newResponses);
      setPhase(PHASE.PRESENTATION);
    }
  }, [seqIndex, secuencia]);

  // Arranca el test cuando llega la config
  useEffect(() => {
    if (phase !== PHASE.LOADING || secuencia.length === 0) return;
    const item = secuencia[0];
    if (item.es_consigna) {
      setPhase(PHASE.CONSIGNA);
    } else if (item.es_pregunta_boolena) {
      setPhase(PHASE.RECOGNITION);
    } else {
      const words = String(item.Valor ?? '').split(';').map((w) => w.trim()).filter(Boolean);
      setWordList(words);
      setWordIndex(0);
      setPhase(PHASE.PRESENTATION);
    }
  }, [phase, secuencia]);

  // Timer de presentación: avanza palabra a palabra
  useEffect(() => {
    if (phase !== PHASE.PRESENTATION) return;
    if (wordIndex >= wordList.length) {
      // terminó la secuencia → recall
      setRecallText('');
      setPhase(PHASE.RECALL);
      return;
    }
    timerRef.current = setTimeout(() => setWordIndex((i) => i + 1), tiempoMili);
    return () => clearTimeout(timerRef.current);
  }, [phase, wordIndex, wordList, tiempoMili]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleConsignaOk = () => {
    advance(responses);
  };

  const handleRecallSubmit = () => {
    const item = secuencia[seqIndex];
    const updated = [...responses, { type: 'recall', item, userAnswer: recallText }];
    advance(updated);
  };

  const handleRecognition = (userYes) => {
    const item = secuencia[seqIndex];
    const updated = [...responses, { type: 'recognition', item, userYes }];
    advance(updated);
  };

  const handleRetry = () => {
    setSeqIndex(0); setWordList([]); setWordIndex(0);
    setRecallText(''); setResponses([]); setShowResults(false);
    setPhase(PHASE.LOADING);
  };

  const recallInputRef = useFocusOnStep(phase === PHASE.RECALL, seqIndex);

  // ── Render ────────────────────────────────────────────────────────────────────

  if (error) return <ErrorScreen message={error} onBack={goBack} />;
  if (phase === PHASE.LOADING || !config) return <LoadingScreen />;

  // DONE
  if (phase === PHASE.DONE) {
    const recallItems    = responses.filter((r) => r.type === 'recall');
    const recogItems     = responses.filter((r) => r.type === 'recognition');
    const reconocidas    = recogItems.filter((r) => r.userYes && r.item.es_verdadero);
    const omisiones      = recogItems.filter((r) => !r.userAnswer && r.item.es_verdadero);
    const falsasAlarmas  = recogItems.filter((r) => r.userAnswer && !r.item.es_verdadero);

    const registros = [
      ...recallItems.map((r) => ({
        pregunta: r.item?.Valor ?? 'Evocación',
        respuesta: '',
        respuestaUsuario: String(r.userAnswer ?? ''),
        correcta: null,
        tiempoRespuesta: '',
      })),
      ...recogItems.map((r) => ({
        pregunta: r.item?.Valor ?? 'Reconocimiento',
        respuesta: r.item?.es_verdadero ? 'SI' : 'NO',
        respuestaUsuario: r.userAnswer ? 'SI' : 'NO',
        correcta: !!r.userAnswer === !!r.item?.es_verdadero,
        tiempoRespuesta: '',
      })),
    ];

    return (
      <DoneScreen conclusiones={showResults ? '' : conclusiones} onRetry={handleRetry} onBack={goBack}>
        <SaveResults tipo={`MCP${testId}`} registros={registros} />
        {showResults ? (
          <div className="mt-4 w-full max-w-lg mx-auto text-left space-y-3">
            {recallItems.map((r, i) => {
              const words  = String(r.item.Valor.split('¿')[0].trim() ?? '').split(';').map((w) => normWord(w.trim().toUpperCase()));
              const typed  = r.userAnswer.toUpperCase().split(/[,\s]+/).map((w) => normWord(w.trim())).filter(Boolean);
              const hits   = typed.filter((w) => words.includes(w));
              const errors = typed.filter((w) => !words.includes(w));
              return (
                <div key={i} className="bg-slate-50 rounded-lg border border-slate-200 p-2">
                  <div className="grid grid-cols-2 divide-x divide-slate-200">
                    {/* Aciertos */}
                    <div className="flex flex-col items-center gap-1 pr-4">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-5 h-5 text-green-500 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-lg font-bold text-slate-800">{hits.length} <span className="text-xs font-normal text-slate-500">de {words.length}</span></span>
                      </div>
                      <p className="text-slate-800 text-xs text-center">
                        {hits.length > 0 ? hits.join(' · ') : <span className="text-slate-400 italic">Sin aciertos</span>}
                      </p>
                    </div>
                    {/* Errores */}
                    <div className="flex flex-col items-center gap-1 pl-4">
                      <div className="flex items-center gap-1.5">
                        <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span className="text-lg font-bold text-slate-800">{errors.length} <span className="text-xs font-normal text-slate-500">de {words.length}</span></span>
                      </div>
                      <p className="text-slate-800 text-xs text-center">
                        {errors.length > 0 ? errors.join(' · ') : <span className="text-slate-400 italic">Sin errores</span>}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            {recogItems.length > 0 && (
              <div className="bg-slate-50 rounded-lg border border-slate-200 p-2">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Reconocimiento</p>
                  <button
                    type="button"
                    onClick={showReconocimientoInfo}
                    className="w-4 h-4 rounded-full bg-slate-300 text-slate-600 text-xs font-bold flex items-center justify-center hover:bg-slate-400 border-0 cursor-pointer leading-none"
                    aria-label="Qué significa cada indicador"
                  >i</button>
                </div>
                <div className="grid grid-cols-3 divide-x divide-slate-200">
                  {/* Reconocidas */}
                  <div className="flex flex-col items-center gap-1 px-2">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm font-bold text-slate-800">{reconocidas.length} <span className="text-xs font-normal text-slate-500">reconocidas</span></span>
                    </div>
                    {reconocidas.length > 0 ? (
                      <div className="flex flex-wrap justify-center gap-1 mt-1">
                        {reconocidas.map((r, j) => (
                          <SafeHtml key={j} as="span" html={r.item.Valor.split('¿')[0].trim()} className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-xs font-medium" />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">Ninguna</p>
                    )}
                  </div>
                  {/* Omisiones */}
                  <div className="flex flex-col items-center gap-1 px-2">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-amber-500 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      <span className="text-sm font-bold text-slate-800">{omisiones.length} <span className="text-xs font-normal text-slate-500">omisiones</span></span>
                    </div>
                    {omisiones.length > 0 ? (
                      <div className="flex flex-wrap justify-center gap-1 mt-1">
                        {omisiones.map((r, j) => (
                          <SafeHtml key={j} as="span" html={r.item.Valor.split('¿')[0].trim()} className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-medium" />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">Ninguna</p>
                    )}
                  </div>
                  {/* Falsas alarmas */}
                  <div className="flex flex-col items-center gap-1 px-2">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-sm font-bold text-slate-800">{falsasAlarmas.length} <span className="text-xs font-normal text-slate-500">falsas alarmas</span></span>
                    </div>
                    {falsasAlarmas.length > 0 ? (
                      <div className="flex flex-wrap justify-center gap-1 mt-1">
                        {falsasAlarmas.map((r, j) => (
                          <SafeHtml key={j} as="span" html={r.item.Valor.split('¿')[0].trim()} className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-xs font-medium" />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">Ninguna</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-center">
              <button
                onClick={() => setShowResults(false)}
                className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 cursor-pointer uppercase"
              >
                Ocultar resultados
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowResults(true)}
            className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 mt-2 cursor-pointer"
          >
            QUIERO SABER MIS RESULTADOS
          </button>
        )}
      </DoneScreen>
    );
  }

  const currentItem = secuencia[seqIndex];

  // CONSIGNA
  if (phase === PHASE.CONSIGNA) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-4xl text-center">
          <SafeHtml
            as="p"
            html={currentItem?.Valor}
            className="text-center text-slate-800 mb-10 whitespace-pre-line leading-tight"
            style={{ fontSize: '33px', fontWeight: 700 }}
          />
          <button
            onClick={handleConsignaOk}
            className="border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity"
          >
            <img src="/assets/img/Ok-icon.png" alt="OK" className="h-20 w-auto" />
          </button>
        </div>
      </div>
    );
  }

  // PRESENTATION
  if (phase === PHASE.PRESENTATION) {
    const word = wordIndex < wordList.length ? wordList[wordIndex] : '';
    return (
      <TestLayout current={wordIndex + 1} total={wordList.length} onExit={handleExit}>
        <SafeHtml
          as="div"
          html={word}
          className="text-8xl font-medium text-center text-slate-800 min-h-[5rem] flex items-center justify-center"
        />
      </TestLayout>
    );
  }

  // RECALL
  if (phase === PHASE.RECALL) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          <h2 className="text-2xl font-bold text-slate-800 mb-4 text-center">
            Escriba todas las palabras que recuerde
            <div className='text-lg text-slate-400 font-light'>separadas por coma o espacio</div>
          </h2>
          <textarea
            ref={recallInputRef}
            value={recallText}
            onChange={(e) => setRecallText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleRecallSubmit(); } }}
            className="w-full min-h-[140px] rounded-lg bg-white border border-slate-300 text-slate-800 p-4 text-xl focus:border-emerald-500 outline-none"
            placeholder="palabra1, palabra2, ..."
          />
          <div className="flex justify-center mt-4">
            <button
              onClick={handleRecallSubmit}
              className="border-0 bg-transparent cursor-pointer p-0 hover:opacity-80 transition-opacity"
            >
              <img src="/assets/img/Ok-icon.png" alt="Continuar" className="h-20 w-auto" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // RECOGNITION
  if (phase === PHASE.RECOGNITION) {
    return (
      <TestLayout current={seqIndex + 1} total={secuencia.length} onExit={handleExit}>
        <p className="text-slate-500 mb-2 text-sm">¿Esta palabra estaba en la lista?</p>
        <SafeHtml as="div" html={currentItem?.Valor} className="text-4xl font-bold text-slate-800 mb-4" />
        <YesNoButtons
          onYes={() => handleRecognition(true)}
          onNo={() => handleRecognition(false)}
        />
      </TestLayout>
    );
  }

  return null;
}
